import { Column, GridApi, IRowNode, SortDirection } from 'ag-grid-community';

class GridApiService {
  private gridApi: GridApi | null = null;

  setGridApi(api: GridApi): void {
    this.gridApi = api;
  }

  getGridApi(): GridApi | null {
    return this.gridApi;
  }
}
export const configForInitialsColumn = {
  width: 80,
  minWidth: 80,
  flex: 0,
};

export const gridApiService = new GridApiService();

function getSortKey(column: Column): string | undefined {
  // when there are multiple columns with the same field, the showGroup of grouped row will be like this: showGroup_1, showGroup_2, etc. please refer to AnticipatedPaymentsReport
  // we need to remove the _1, _2, etc.
  return (
    (column?.getColDef()?.showRowGroup as string)?.replace(/_\d+$/, '') || column?.getUserProvidedColDef()?.field
  );
}

export function getSortSelector(allowThreeGroups: boolean = false) {
  const gridApi = gridApiService.getGridApi();
  if (!gridApi) {
    return { sorting: [] };
  }
  const columns = gridApi.getAllGridColumns();
  const rowGroupColumns = gridApi.getRowGroupColumns();

  const sortedColumns = columns
    .filter((column) => !!column.getSort())
    .sort((a, b) => a.getSortIndex() - b.getSortIndex());

  // In singleColumn mode, sort lives on the auto-group column only. Map that sort onto
  // the underlying row-group fields for the backend — without mutating the live grid.
  // Mutating via applyColumnState previously left stale Descending on first export (clones
  // never received the new sort) and duplicated StaffName/TaskCode on later exports.
  const inheritedSortByColId = new Map<string, SortDirection>();
  const mode = gridApi.getGridOption('groupDisplayType');
  if (mode === 'singleColumn') {
    const rowGroupColIds = new Set(rowGroupColumns.map((column) => column.getColId()));

    // Drop any hidden row-group columns that already carry sort (e.g. leftover pollution).
    for (let i = sortedColumns.length - 1; i >= 0; i--) {
      if (rowGroupColIds.has(sortedColumns[i].getColId())) {
        sortedColumns.splice(i, 1);
      }
    }

    const groupColumnsIndex = sortedColumns.findIndex((column) => {
      return column.getColDef()?.showRowGroup;
    });
    if (groupColumnsIndex > -1) {
      const groupSort = sortedColumns[groupColumnsIndex].getSort();
      rowGroupColumns.forEach((column) => {
        inheritedSortByColId.set(column.getColId(), groupSort);
      });
      sortedColumns.splice(groupColumnsIndex, 1, ...rowGroupColumns);
    }
  }
  const sorting = sortedColumns.map((column) => {
    const sort = inheritedSortByColId.has(column.getColId())
      ? inheritedSortByColId.get(column.getColId())
      : column?.getSort();
    return {
      Key: getSortKey(column),
      Descending: sort === 'desc',
    };
  });

  const extraParams = {};

  const hasSumTypeAggColumns = sortedColumns.some((column) => column.getColDef()?.aggFunc === 'sum');

  const groups =
    rowGroupColumns
      .map((column) => {
        return {
          // @ts-ignore
          Key: column?.getUserProvidedColDef()?.groupingKeyForBackend || column?.getUserProvidedColDef()?.field,
        };
      })
      ?.filter((item) => item.Key !== 'DoNotPassGroupingKeyToBackend') || [];
  if (groups.length && sorting.length && hasSumTypeAggColumns) {
    Object.assign(extraParams, {
      // only the last 2 levels of groups are used for sorting
      groups: allowThreeGroups ? groups : groups.slice(-2),
      aggregations: groups.length > 2 ? sorting.slice(1) : sorting,
      sorting: groups.length > 2 ? sorting.slice(1) : sorting,
    });
  }
  return {
    sorting,
    ...extraParams,
  };
}

export const getFirstChildrenValue = (node: IRowNode, key: string) => {
  if (!node || !node.childrenAfterSort?.length) {
    return null;
  }
  if (node.childrenAfterSort?.length) {
    if (node.childrenAfterSort[0]?.data) {
      return node.childrenAfterSort[0]?.data[key];
    } else {
      return getFirstChildrenValue(node.childrenAfterSort[0], key);
    }
  }
};
